import React from 'react';
import { Link } from 'react-router-dom';
import { FaLock, FaArrowLeft, FaShieldAlt, FaEye, FaDatabase, FaCookieBite, FaUserShield } from 'react-icons/fa';
import SEO from '../components/SEO';

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Privacy Policy - Your Data, Your Control"
        description="MirageDocs Privacy Policy. Zero-knowledge architecture means we never see your files. GDPR compliant. Learn what data we collect and your rights."
        url="https://miragedocs.com/privacy"
      />
      {/* Header */}
      <header className="w-full py-6 px-6 bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FaLock className="text-[#2186EB]" />
            MirageDocs
          </Link>
          <Link to="/" className="text-gray-600 hover:text-gray-900 flex items-center gap-2">
            <FaArrowLeft /> Back
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <FaShieldAlt className="text-4xl text-[#2186EB]" />
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Privacy Policy</h1>
          </div>

          <p className="text-gray-600 mb-8">
            Last updated: December 14, 2025
          </p>

          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FaEye className="text-[#2186EB]" />
                Our Privacy Commitment
              </h2>
              <p className="text-gray-700 mb-4">
                MirageDocs is built on a foundation of privacy. Unlike traditional file sharing services,
                we have designed our system so that <strong>we cannot access your files</strong>. This isn't just
                a policy—it's architecturally enforced through end-to-end encryption.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-blue-800 font-medium">
                  Zero-Knowledge Architecture: Your files are encrypted in your browser before upload.
                  We never have access to your plaintext files. Your encryption key is sent once, over
                  TLS, solely so we can compute a one-way verification hash of it - we never store the
                  key itself, and it cannot be recovered from what we store.
                </p>
              </div>
            </section>

            {/* What We Collect */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FaDatabase className="text-[#2186EB]" />
                What We Collect
              </h2>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">For Anonymous Users</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Encrypted file data (we cannot decrypt this)</li>
                <li>File metadata: original filename, file size, expiry settings</li>
                <li>Basic request logs (IP addresses retained for 24 hours for abuse prevention)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">For Registered Users</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Email address (for account and optional notifications)</li>
                <li>Hashed password (using bcrypt, we cannot see your password)</li>
                <li>Subscription status and billing information (processed by Stripe)</li>
                <li>Usage statistics (shares count, portal count)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">What We Never Collect</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Unencrypted file contents</li>
                <li>Encryption keys</li>
                <li>File passwords (SRP verification data only)</li>
                <li>Tracking cookies or advertising identifiers</li>
                <li>Browsing history outside our service</li>
              </ul>
            </section>

            {/* Cookies */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FaCookieBite className="text-[#2186EB]" />
                Cookies
              </h2>
              <p className="text-gray-700 mb-4">
                We use only essential cookies required for the service to function:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Authentication cookies</strong>: To keep you logged in (httpOnly, secure)</li>
                <li><strong>CSRF tokens</strong>: To protect against cross-site request forgery</li>
              </ul>
              <p className="text-gray-700 mt-4">
                We do not use analytics cookies, advertising cookies, or any third-party tracking.
              </p>
            </section>

            {/* Data Retention */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Retention</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Shared files</strong>: Automatically deleted after expiry or download limit is reached</li>
                <li><strong>Account data</strong>: Retained while your account is active; deleted within 30 days of account deletion</li>
                <li><strong>Server logs</strong>: IP addresses retained for 24 hours, then anonymized</li>
                <li><strong>Billing records</strong>: Retained as required by Luxembourg law (typically 10 years)</li>
              </ul>
            </section>

            {/* Third Parties */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Services</h2>
              <p className="text-gray-700 mb-4">
                We use minimal third-party services:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Stripe</strong>: Payment processing. Stripe receives only the data necessary for payments.
                  See <a href="https://stripe.com/privacy" className="text-[#2186EB] hover:underline" target="_blank" rel="noopener noreferrer">Stripe's Privacy Policy</a>.
                </li>
                <li><strong>Email provider</strong>: For transactional emails (account verification, notifications).
                  We send only essential data.</li>
              </ul>
              <p className="text-gray-700 mt-4">
                We do not sell, rent, or share your data with advertisers or data brokers.
              </p>
            </section>

            {/* Your Rights */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FaUserShield className="text-[#2186EB]" />
                Your Rights (GDPR)
              </h2>
              <p className="text-gray-700 mb-4">
                Under GDPR and Luxembourg law, you have the right to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Access</strong>: Request a copy of your personal data</li>
                <li><strong>Rectification</strong>: Correct inaccurate personal data</li>
                <li><strong>Erasure</strong>: Request deletion of your account and data</li>
                <li><strong>Portability</strong>: Export your data in a machine-readable format</li>
                <li><strong>Objection</strong>: Object to certain processing activities</li>
                <li><strong>Restriction</strong>: Request limited processing of your data</li>
              </ul>
              <p className="text-gray-700 mt-4">
                To exercise these rights, contact us at <a href="mailto:privacy@miragedocs.com" className="text-[#2186EB] hover:underline">privacy@miragedocs.com</a>.
              </p>
            </section>

            {/* Security */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Security Measures</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>AES-256 client-side encryption for all files</li>
                <li>TLS 1.2/1.3 for all data in transit</li>
                <li>SRP protocol for password-protected files (zero-knowledge verification)</li>
                <li>bcrypt password hashing for user accounts</li>
                <li>Regular security audits and updates</li>
              </ul>
              <p className="text-gray-700 mt-4">
                For detailed security information, see our <Link to="/security" className="text-[#2186EB] hover:underline">Security Documentation</Link>.
              </p>
            </section>

            {/* Contact */}
            <section className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700">
                For privacy inquiries or to exercise your data rights:
              </p>
              <p className="text-gray-700 mt-2">
                <strong>Email:</strong> <a href="mailto:privacy@miragedocs.com" className="text-[#2186EB] hover:underline">privacy@miragedocs.com</a>
              </p>
              <p className="text-gray-700 mt-2">
                <strong>Data Controller:</strong> MirageDocs<br />
                Luxembourg
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 bg-gray-900 text-gray-400">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 text-white mb-4">
            <FaLock />
            <span className="font-bold">MirageDocs</span>
          </div>
          <div className="flex justify-center gap-6 text-sm">
            <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white">Terms of Service</Link>
            <Link to="/security" className="hover:text-white">Security</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
